import React, { useRef, useState } from 'react';
import {
  Card,
  Typography,
  IconButton,
  Box,
  CardHeader,
  Grid2,
  Tooltip,
} from '@mui/material';
import {
  ContextMenu,
  FormExtendIcon,
  FormGlobalIcon,
  FormLocalIcon,
  FormLockedIcon,
  InstallerIcon,
  SupervisorIcon,
  TesterIcon,
  theme,
} from '@konecorp/ui-library';
import FormCardMenuComponent from '../FormCardMenu';
import FormCardContent from '../FormCardContent';
import { Constants } from '../../services/service';
import { FormContextProvider } from '../../formContext';
import { styled } from '@mui/material/styles';
import { FbeFrontline } from '../../services/fbeTypes';

export type FormCardProps = {
  pk: string;
  sk: string;
  accessToken: string;
  createdDate: string;
  createdTime: string;
  createdBy: string;
  updatedDate: string;
  updatedTime: string;
  updatedBy: string;
  status: string;
  activeStatus: string;
  title: string;
  description: string;
  roles: string[];
  formType?: string;
  isExtension?: boolean;
  parentFormName?: string;
  hasExtension: boolean;
  isLocalAdmin: boolean;
  isGlobalAdmin: boolean;
  active: boolean;
  languages: string[];
  frontline?: FbeFrontline;
};

const CardContainer = styled(Card)(({ theme }) => ({
  maxWidth: 500,
  maxHeight: 500,
  margin: theme.spacing(2),
  position: 'relative',
}));

const FormCard = (props: FormCardProps): JSX.Element => {
  const {
    pk,
    sk,
    accessToken,
    createdDate,
    createdTime,
    createdBy,
    updatedDate,
    updatedBy,
    updatedTime,
    status,
    activeStatus,
    title,
    description,
    roles,
    formType,
    isExtension,
    parentFormName,
    hasExtension,
    isLocalAdmin,
    isGlobalAdmin,
    active,
    languages,
    frontline,
  } = props;

  const isDraft = status === Constants.DRAFT;
  const isDeactivated = activeStatus === Constants.DEACTIVATED;
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const contextMenuRef = useRef<HTMLElement | null>(null);

  const handleContextMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuAction = (action: string) => {
    console.warn(action);
    handleCloseMenu();
  };

  const renderRoleIcons = () => {
    const validSpvRoles = ['sebSupervisor', 'nebSupervisor'];
    const hasValidspvRole = validSpvRoles.some((role) => roles.includes(role));
    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 1,
          display: 'flex',
          marginRight: 0,
          paddingRight: '14px',
          height:'28px'
        }}
      >
        <Grid2 container direction="row">
          {roles.includes('tester') && (
            <Tooltip title="Tester">
              <Grid2 >
                <TesterIcon data-testid="TesterIcon"/>
              </Grid2>
            </Tooltip>
          )}
          {roles.includes('installer') && (
            <Tooltip title="Installer">
              <Grid2>
                <InstallerIcon data-testid="InstallerIcon" />
              </Grid2>
            </Tooltip>
          )}
          {hasValidspvRole && (
            <Tooltip title="Supervisor">
              <Grid2>
                <SupervisorIcon data-testid="SupervisorIcon" />
              </Grid2>
            </Tooltip>
          )}
        </Grid2>
      </Box>
    );
  };

  const renderFormIcon = () => {
    if (isExtension) {
      return <FormExtendIcon data-testid="FormExtendIcon" />;
    }
    switch (formType) {
      case Constants.LOCAL:
        return <FormLocalIcon data-testid="FormLocalIcon" />;
      case Constants.LOCKED:
        return <FormLockedIcon data-testid="FormLockedIcon" />;
      default:
        return <FormGlobalIcon data-testid="FormGlobalIcon" />;
    }
  };

  const renderBottomLeftText = () => {
    if (isExtension) {
      return (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            maxWidth: '80%', // Optional: constrain width so long text wraps
          }}
        >
        <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              paddingLeft:'15px',
              mb: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {parentFormName}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <CardContainer data-testid="form-card">
      <Box
        sx={{
          backgroundColor:
            formType === Constants.LOCAL || formType === Constants.EXTENDED
              ? '#CAF3FE'
              : '#9cddc1',
          height: (status === Constants.LIVE && !isDeactivated)? '14px' : '28px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 16px',
        }}
      >
        {status !== Constants.LIVE  && (
          <Box
            sx={{
              backgroundColor: isDraft ? '#f8d7da' : 'yellow',
              border: '1px solid black',
              padding: '1px 12px',
              borderRadius: '4px',
              fontWeight: 'bold',
              fontSize: '10px',
            }}
          >
            {isDraft ? 'Draft' : 'Pending Changes'}
          </Box>
        )}
        {isDeactivated && (
          <Box
            sx={{
              backgroundColor: '#E3F2FD',
              border: '1px solid black',
              padding: '1px 12px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 'bold',
            }}
          >
            Deactivated
          </Box>
        )}
      </Box>

      <CardHeader
        sx={{
          padding: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
        title={
        <Grid2 container alignItems="center" justifyContent="space-between">

          {/* Left Section: Icon + Title */}
          <Grid2 size={12} container alignItems="center">

            {/* Form Icon */}
            <Grid2 size={1}
              sx={{
                width:'10%',
                color: 'black',
                marginRight: theme.spacing(1),
                marginLeft: theme.spacing(2),
              }}
            >
              {renderFormIcon()}
            </Grid2>

            {/* Title with Tooltip */}
            <Grid2 size={8}>
            <Tooltip title={title.trim()}>
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
                <Typography
                  variant="h6"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {title.trim().substring(0,35)}
                </Typography>
                </Box>
                </Tooltip>
            </Grid2>

            {/* Right Section: Context Menu Button */}
            <Grid2 size={1} container justifyContent={'left'}>
              <IconButton
                ref={contextMenuRef as React.RefObject<HTMLButtonElement>}
                onClick={handleContextMenuClick}
                sx={{ color: 'black', display:'block'}}
              >
                &nbsp;<ContextMenu data-testid="ContextMenu" />
              </IconButton>
            </Grid2>
          </Grid2>
        </Grid2>
        }
      />

      <div
        style={{
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      >
        <hr
          style={{
            border: '0',
            borderTop: '1px solid #d3d3d3',
            margin: 0,
            width: '100%',
          }}
        />
      </div>

      <FormCardContent
        description={description}
        createdDate={createdDate}
        createdTime={createdTime}
        createdBy={createdBy}
        updatedDate={updatedDate}
        updatedTime={updatedTime}
        updatedBy={updatedBy}
        formType={formType}
        status={status}
        title={title}
        roles={roles}
      />
      {renderBottomLeftText()}
      {renderRoleIcons()}
      <FormContextProvider>
        <FormCardMenuComponent
          frontline={frontline}
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          onAction={handleMenuAction}
          pk={pk}
          sk={sk}
          active={active}
          title={title}
          hasExtension={hasExtension}
          accessToken={accessToken}
          isLocalAdmin={isLocalAdmin}
          isGlobalAdmin={isGlobalAdmin}
          languages={languages}
        />
      </FormContextProvider>
    </CardContainer>
  );
};

export default FormCard;
